import { useState } from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { DataService } from "../utils/dataService";

// El código del vendedor viaja en la URL del servicio.
export const buildSellerCodeEndpoint = (code: string) =>
	`/agents/${encodeURIComponent(code)}`;

export const SELLER_CODE_SOURCE = "sellercodeterminal";

// Único estado con el que se acepta un agente.
const AGENT_ACTIVE_STATUS = "ACTIVE";

export type SellerCodeAgent = {
	id?: string;
	agentCode?: string;
	agencyCode?: string;
	documentNumber?: string;
	status?: string;
	registrationDate?: string | null;
	retirementDate?: string | null;
};

export type SellerCodeResponse = {
	success?: boolean;
	titleResponse?: string;
	textResponse?: string;
	lastAction?: string;
	data?: SellerCodeAgent;
};

export type SellerCodeResult = {
	valid: boolean;
	unreachable: boolean;
	response: SellerCodeResponse | null;
};

type UseSellerCodeValidationResult = {
	validateSellerCode: (code: string) => Promise<SellerCodeResult>;
	loading: boolean;
};

/**
 * Verifica el código de vendedor contra `/agents/:code` antes de dar paso al
 * checkout.
 */
const useSellerCodeValidation = (
	clientId?: string | number
): UseSellerCodeValidationResult => {
	const [loading, setLoading] = useState(false);

	const validateSellerCode = async (
		code: string
	): Promise<SellerCodeResult> => {
		setLoading(true);
		try {
			const _dataService = new DataService();
			const res = await _dataService.post(
				buildSellerCodeEndpoint(String(code ?? "").trim()),
				{ source: SELLER_CODE_SOURCE, clientId }
			);
			const response = (res?.data as SellerCodeResponse) ?? null;
			return {
				valid: isAgentValid(response),
				unreachable: false,
				response,
			};
		} catch (error) {
			const response = (error?.response?.data as SellerCodeResponse) ?? null;
			if (response) {
				return {
					valid: isAgentValid(response),
					unreachable: false,
					response,
				};
			}
			datadogLogs.logger.info("Error verificando el código de vendedor", {
				error: error?.message,
			});
			return { valid: false, unreachable: true, response: null };
		} finally {
			setLoading(false);
		}
	};

	return { validateSellerCode, loading };
};

const isAgentValid = (response: SellerCodeResponse | null): boolean => {
	if (response?.success !== true) return false;
	const status = response?.data?.status;
	return status === undefined || status === AGENT_ACTIVE_STATUS;
};

export default useSellerCodeValidation;
